/**
 * ProgramExplorer - Component to browse the CAFOP program and generate lessons.
 * Now with database persistence for mobile app.
 */

import React, { useState, useEffect } from 'react';
import {
  ChevronRight,
  ChevronDown,
  Loader2,
  Sparkles,
  Save,
  Database,
  Check,
  AlertCircle,
  RefreshCw,
  Trash2,
  CheckCircle,
  Eye,
} from 'lucide-react';
import {
  getProgramStructure,
  generateLesson,
  saveLessonToDatabase,
  addExercisesToLesson,
  generateMoreExercises,
  deleteLesson,
  getLessonsSummary,
  getLessonFromDatabase,
  updateLesson,
  type ProgramPart,
  type Lesson,
} from '../services/learningApi';
import LessonView from './learning/LessonView';
import LessonEditor from './learning/LessonEditor';

interface ProgramExplorerProps {
  onLessonGenerated?: (lesson: Lesson) => void;
}

const ProgramExplorer: React.FC<ProgramExplorerProps> = ({ onLessonGenerated }) => {
  const [program, setProgram] = useState<Record<string, ProgramPart>>({});
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [expandedParts, setExpandedParts] = useState<Set<string>>(new Set());
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [expandedSkills, setExpandedSkills] = useState<Set<string>>(new Set());
  const [generatingLesson, setGeneratingLesson] = useState<string | null>(null);
  const [currentLesson, setCurrentLesson] = useState<Lesson | null>(null);
  const [currentLessonMeta, setCurrentLessonMeta] = useState<{
    partId: string;
    moduleId: string;
    skillId: string;
    lessonId: string;
    partName: string;
    moduleName: string;
    skillName: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Database state
  const [saving, setSaving] = useState(false);
  const [savedToDb, setSavedToDb] = useState(false);
  const [lessonExistsInDb, setLessonExistsInDb] = useState<Record<string, number>>({});
  const [addingExercises, setAddingExercises] = useState(false);
  const [deletingLesson, setDeletingLesson] = useState<string | null>(null);
  const [loadingLesson, setLoadingLesson] = useState<string | null>(null);
  const [isEditorMode, setIsEditorMode] = useState(false);
  const [updatingLesson, setUpdatingLesson] = useState(false);

  useEffect(() => {
    loadProgram();
  }, []);

  const loadProgram = async () => {
    try {
      setLoading(true);
      const result = await getProgramStructure();
      setProgram(result.program);
      setStats(result.statistics);
      
      // Check which lessons already exist in database (single API call!)
      await checkExistingLessons();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const checkExistingLessons = async () => {
    try {
      // Single API call to get all existing lessons
      const result = await getLessonsSummary();
      
      if (result.success && result.lessons) {
        console.log(`✅ Loaded ${Object.keys(result.lessons).length} existing lessons from DB`);
        setLessonExistsInDb(result.lessons);
      }
    } catch (err) {
      console.error('Failed to load existing lessons:', err);
      // Don't block the UI if this fails
    }
  };

  const togglePart = (partId: string) => {
    const newExpanded = new Set(expandedParts);
    if (newExpanded.has(partId)) {
      newExpanded.delete(partId);
    } else {
      newExpanded.add(partId);
    }
    setExpandedParts(newExpanded);
  };

  const toggleModule = (moduleId: string) => {
    const newExpanded = new Set(expandedModules);
    if (newExpanded.has(moduleId)) {
      newExpanded.delete(moduleId);
    } else {
      newExpanded.add(moduleId);
    }
    setExpandedModules(newExpanded);
  };

  const toggleSkill = (skillId: string) => {
    const newExpanded = new Set(expandedSkills);
    if (newExpanded.has(skillId)) {
      newExpanded.delete(skillId);
    } else {
      newExpanded.add(skillId);
    }
    setExpandedSkills(newExpanded);
  };

  const handleGenerateLesson = async (
    partId: string,
    partName: string,
    moduleId: string,
    moduleName: string,
    skillId: string,
    skillName: string,
    lessonTitle: string,
    lessonNumber: number,
    subtitle?: string
  ) => {
    const lessonKey = `${partId}-${lessonNumber}-${lessonTitle}`;
    setGeneratingLesson(lessonKey);
    setError(null);
    setSavedToDb(false);

    try {
      const result = await generateLesson({
        part_name: partName,
        module_name: moduleName,
        skill_name: skillName,
        lesson_title: lessonTitle,
        lesson_number: lessonNumber,
        context: subtitle,
        num_exercises: 10,
      });

      if (result.success && result.lesson) {
        setCurrentLesson(result.lesson);
        setCurrentLessonMeta({
          partId,
          moduleId,
          skillId,
          lessonId: lessonTitle.toLowerCase().replace(/\s+/g, '_').replace(/[/\\]/g, '_'),
          partName,
          moduleName,
          skillName,
        });
        onLessonGenerated?.(result.lesson);
      } else {
        setError(result.message);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setGeneratingLesson(null);
    }
  };

  // Sauvegarder la leçon en base
  const handleSaveToDatabase = async () => {
    if (!currentLesson || !currentLessonMeta) return;

    setSaving(true);
    setError(null);

    try {
      const result = await saveLessonToDatabase({
        subject_name: currentLessonMeta.partName,  // Nom du sujet (Français, Mathématiques, etc.)
        module_name: currentLessonMeta.moduleName,
        skill_name: currentLessonMeta.skillName,
        title: currentLesson.title,
        subtitle: currentLesson.subtitle,
        lesson_number: currentLesson.number,
        rule: (currentLesson.content?.rule as unknown as Record<string, unknown>) || {},
        tips: (currentLesson.content?.tips as unknown as Array<unknown>) || [],
        examples: (currentLesson.content?.examples as unknown as Array<Record<string, unknown>>) || [],
        exercises: (currentLesson.exercises as unknown as Array<Record<string, unknown>>) || [],
      });

      if (result.success) {
        setSavedToDb(true);
        // Stocker l'ID de la leçon sauvegardée
        if (result.lesson_id) {
          setLessonExistsInDb(prev => ({
            ...prev,
            [currentLesson.title]: result.exercises_count || 0
          }));
        }
      } else {
        setError(result.message || 'Erreur de sauvegarde');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setSaving(false);
    }
  };

  // Générer et ajouter plus d'exercices
  const handleAddMoreExercises = async () => {
    if (!currentLesson || !currentLessonMeta) return;

    setAddingExercises(true);
    setError(null);

    try {
      // D'abord générer de nouveaux exercices
      const generateResult = await generateMoreExercises({
        lesson_id: currentLesson.id,
        lesson_title: currentLesson.title,
        rule_summary: currentLesson.content?.rule?.rule || currentLesson.content?.rule?.title || '',
        count: 10,
        difficulty: 'medium',
        contexts: ['école', 'famille', 'travail'],
        exclude_sentences: currentLesson.exercises.map((e: { content?: { sentence?: string } }) => e.content?.sentence).filter(Boolean) as string[],
      });

      if (generateResult.success && generateResult.exercises.length > 0) {
        // Ajouter à la leçon courante
        const updatedLesson = {
          ...currentLesson,
          exercises: [...currentLesson.exercises, ...generateResult.exercises]
        };
        setCurrentLesson(updatedLesson);

        // Sauvegarder en base si déjà sauvegardée - utiliser le titre comme identifiant
        if (savedToDb) {
          await addExercisesToLesson(currentLesson.title, generateResult.exercises as unknown as Array<Record<string, unknown>>);
          
          // Mettre à jour le compteur
          setLessonExistsInDb(prev => ({
            ...prev,
            [currentLesson.title]: (prev[currentLesson.title] || 0) + generateResult.exercises.length
          }));
        }
      } else {
        setError('Impossible de générer de nouveaux exercices');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setAddingExercises(false);
    }
  };

  // Charger une leçon depuis la base de données
  const handleViewLesson = async (
    partId: string,
    partName: string,
    moduleId: string,
    moduleName: string,
    skillId: string,
    skillName: string,
    lessonTitle: string,
    _lessonNumber: number,
    _subtitle?: string
  ) => {
    setLoadingLesson(lessonTitle);
    setError(null);

    try {
      const result = await getLessonFromDatabase(lessonTitle);
      
      if (result.success && result.lesson) {
        setCurrentLesson(result.lesson);
        setCurrentLessonMeta({
          partId,
          moduleId,
          skillId,
          lessonId: lessonTitle.toLowerCase().replace(/\s+/g, '_').replace(/[/\\]/g, '_'),
          partName,
          moduleName,
          skillName,
        });
        setSavedToDb(true); // Déjà en DB
        setIsEditorMode(true); // Ouvrir en mode édition
      } else {
        setError('Erreur lors du chargement de la leçon');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement');
    } finally {
      setLoadingLesson(null);
    }
  };

  // Sauvegarder les modifications d'une leçon
  const handleUpdateLesson = async (updatedLesson: any) => {
    if (!currentLesson) return;
    
    setUpdatingLesson(true);
    setError(null);

    try {
      const result = await updateLesson(currentLesson.title, updatedLesson);
      
      if (result.success) {
        setCurrentLesson(updatedLesson);
        
        // Mettre à jour le compteur d'exercices dans l'état
        setLessonExistsInDb(prev => ({
          ...prev,
          [currentLesson.title]: updatedLesson.exercises.length
        }));
        
        alert(`✓ Leçon mise à jour avec succès !`);
        setIsEditorMode(false); // Fermer l'éditeur
      } else {
        setError('Erreur lors de la mise à jour');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la mise à jour');
    } finally {
      setUpdatingLesson(false);
    }
  };

  // Supprimer une leçon de la base
  const handleDeleteLesson = async (lessonTitle: string) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer la leçon "${lessonTitle}" et tous ses exercices ?`)) {
      return;
    }

    setDeletingLesson(lessonTitle);
    setError(null);

    try {
      const result = await deleteLesson(lessonTitle);
      
      if (result.success) {
        // Retirer de l'état
        setLessonExistsInDb(prev => {
          const newState = { ...prev };
          delete newState[lessonTitle];
          return newState;
        });
        
        // Si c'est la leçon courante, la fermer
        if (currentLesson?.title === lessonTitle) {
          setCurrentLesson(null);
          setCurrentLessonMeta(null);
          setSavedToDb(false);
        }
        
        alert(`✓ ${result.message}`);
      } else {
        setError(result.message || 'Erreur de suppression');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setDeletingLesson(null);
    }
  };

  const handleLessonComplete = (score: number) => {
    console.log('Lesson completed with score:', score);
    // Proposer de sauvegarder si pas encore fait
    if (!savedToDb) {
      handleSaveToDatabase();
    }
    setCurrentLesson(null);
    setCurrentLessonMeta(null);
    setSavedToDb(false);
  };

  const handleRequestMoreExercises = () => {
    handleAddMoreExercises();
  };

  const handleRequestExplanation = (errors: string[]) => {
    console.log('Request explanation for errors:', errors);
    // TODO: Implement
  };

  const handleStartChallenge = () => {
    console.log('Start challenge');
    // TODO: Implement
  };

  const handleBackToProgram = () => {
    setCurrentLesson(null);
    setCurrentLessonMeta(null);
    setSavedToDb(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Chargement du programme...</span>
      </div>
    );
  }

  if (currentLesson && !isEditorMode) {
    return (
      <div>
        {/* Header avec actions */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={handleBackToProgram}
            className="text-blue-600 hover:underline flex items-center gap-1"
          >
            ← Retour au programme
          </button>
          
          <div className="flex items-center gap-3">
            {/* Compteur d'exercices */}
            <span className="text-sm text-gray-500">
              {currentLesson.exercises?.length || 0} exercices
            </span>
            
            {/* Bouton ajouter exercices */}
            <button
              onClick={handleAddMoreExercises}
              disabled={addingExercises}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition disabled:opacity-50"
            >
              {addingExercises ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Génération...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  +10 exercices
                </>
              )}
            </button>

            {/* Bouton sauvegarder */}
            {!savedToDb ? (
              <button
                onClick={handleSaveToDatabase}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sauvegarde...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Sauvegarder en base
                  </>
                )}
              </button>
            ) : (
              <div className="flex items-center gap-2 px-4 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg">
                <Check className="w-4 h-4" />
                Sauvegardé ✓
              </div>
            )}
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <LessonView
          lesson={currentLesson}
          onComplete={handleLessonComplete}
          onRequestMoreExercises={handleRequestMoreExercises}
          onRequestExplanation={handleRequestExplanation}
          onStartChallenge={handleStartChallenge}
        />
      </div>
    );
  }

  return (
    <>
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 mb-6 text-white">
        <h1 className="text-2xl font-bold mb-2">📚 Programme CAFOP 2021-2022</h1>
        <p className="text-blue-100 mb-4">
          Système d'apprentissage progressif avec génération IA
        </p>
        {stats && (
          <div className="flex gap-6 text-sm">
            <div>
              <span className="text-blue-200">Parties:</span>{' '}
              <span className="font-semibold">{stats.total_parts}</span>
            </div>
            <div>
              <span className="text-blue-200">Modules:</span>{' '}
              <span className="font-semibold">{stats.total_modules}</span>
            </div>
            <div>
              <span className="text-blue-200">Skills:</span>{' '}
              <span className="font-semibold">{stats.total_skills}</span>
            </div>
            <div>
              <span className="text-blue-200">Leçons:</span>{' '}
              <span className="font-semibold">{stats.total_lessons}</span>
            </div>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Program tree */}
      <div className="space-y-4">
        {Object.entries(program).map(([partId, part]) => (
          <div
            key={partId}
            className="border border-gray-200 rounded-lg overflow-hidden"
          >
            {/* Part header */}
            <button
              onClick={() => togglePart(partId)}
              className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition"
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">{part.icon}</span>
                <span className="font-semibold text-gray-900">{part.name}</span>
              </div>
              {expandedParts.has(partId) ? (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {/* Modules */}
            {expandedParts.has(partId) && (
              <div className="p-4 space-y-3">
                {Object.entries(part.modules).map(([moduleId, module]) => (
                  <div
                    key={moduleId}
                    className="border border-gray-100 rounded-lg overflow-hidden"
                  >
                    {/* Module header */}
                    <button
                      onClick={() => toggleModule(`${partId}-${moduleId}`)}
                      className="w-full flex items-center justify-between p-3 bg-white hover:bg-gray-50 transition"
                    >
                      <div className="flex items-center gap-2">
                        <span>{module.icon}</span>
                        <span className="font-medium text-gray-800">
                          {module.name}
                        </span>
                      </div>
                      {expandedModules.has(`${partId}-${moduleId}`) ? (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      )}
                    </button>

                    {/* Skills */}
                    {expandedModules.has(`${partId}-${moduleId}`) && (
                      <div className="p-3 bg-gray-50 space-y-2">
                        {Object.entries(module.skills).map(([skillId, skill]) => (
                          <div
                            key={skillId}
                            className="bg-white rounded-lg border border-gray-100"
                          >
                            {/* Skill header */}
                            <button
                              onClick={() =>
                                toggleSkill(`${partId}-${moduleId}-${skillId}`)
                              }
                              className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition"
                            >
                              <span className="text-sm font-medium text-gray-700">
                                {skill.name}
                              </span>
                              <span className="text-xs text-gray-400">
                                {skill.lessons.length} leçons
                              </span>
                            </button>

                            {/* Lessons */}
                            {expandedSkills.has(
                              `${partId}-${moduleId}-${skillId}`
                            ) && (
                              <div className="px-3 pb-3 space-y-2">
                                {skill.lessons.map((lesson) => {
                                  const lessonKey = `${partId}-${lesson.number}-${lesson.title}`;
                                  const dbKey = lesson.title; // Utiliser directement le titre comme clé
                                  const exercisesInDb = lessonExistsInDb[dbKey] || 0;
                                  const isGenerating = generatingLesson === lessonKey;
                                  const isDeleting = deletingLesson === dbKey;
                                  const existsInDb = exercisesInDb > 0;

                                  return (
                                    <div
                                      key={lesson.number}
                                      className={`flex items-center justify-between p-2 rounded transition ${
                                        existsInDb ? 'bg-green-50 border border-green-200' : 'bg-gray-50'
                                      }`}
                                    >
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                          {existsInDb && (
                                            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                                          )}
                                          <span className="text-sm font-medium text-gray-800">
                                            Leçon {lesson.number}: {lesson.title}
                                          </span>
                                          {existsInDb && (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
                                              <Database className="w-3 h-3" />
                                              {exercisesInDb} ex.
                                            </span>
                                          )}
                                        </div>
                                        {lesson.subtitle && (
                                          <p className="text-xs text-gray-500">
                                            {lesson.subtitle}
                                          </p>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-2">
                                        {existsInDb && (
                                          <>
                                            <button
                                              onClick={() =>
                                                handleViewLesson(
                                                  partId,
                                                  part.name,
                                                  moduleId,
                                                  module.name,
                                                  skillId,
                                                  skill.name,
                                                  lesson.title,
                                                  lesson.number,
                                                  lesson.subtitle
                                                )
                                              }
                                              disabled={loadingLesson === dbKey}
                                              className="flex items-center gap-1 px-3 py-1 text-sm rounded transition bg-blue-100 text-blue-700 hover:bg-blue-200 disabled:opacity-50"
                                              title="Voir et modifier cette leçon"
                                            >
                                              {loadingLesson === dbKey ? (
                                                <>
                                                  <Loader2 className="w-4 h-4 animate-spin" />
                                                  Chargement...
                                                </>
                                              ) : (
                                                <>
                                                  <Eye className="w-4 h-4" />
                                                  Voir
                                                </>
                                              )}
                                            </button>
                                            <button
                                              onClick={() => handleDeleteLesson(dbKey)}
                                              disabled={isDeleting}
                                              className="flex items-center gap-1 px-2 py-1 text-xs rounded transition bg-red-100 text-red-600 hover:bg-red-200 disabled:opacity-50"
                                              title="Supprimer cette leçon"
                                            >
                                              {isDeleting ? (
                                                <Loader2 className="w-3 h-3 animate-spin" />
                                              ) : (
                                                <Trash2 className="w-3 h-3" />
                                              )}
                                            </button>
                                          </>
                                        )}
                                        <button
                                          onClick={() =>
                                            handleGenerateLesson(
                                              partId,
                                              part.name,
                                              moduleId,
                                              module.name,
                                              skillId,
                                              skill.name,
                                              lesson.title,
                                              lesson.number,
                                              lesson.subtitle
                                            )
                                          }
                                          disabled={isGenerating}
                                          className={`flex items-center gap-1 px-3 py-1 text-sm rounded transition ${
                                            isGenerating
                                              ? 'bg-gray-200 text-gray-500'
                                              : existsInDb
                                              ? 'bg-green-600 text-white hover:bg-green-700'
                                              : 'bg-blue-600 text-white hover:bg-blue-700'
                                          }`}
                                          title={existsInDb ? 'Regénérer cette leçon' : 'Générer cette leçon'}
                                        >
                                          {isGenerating ? (
                                            <>
                                              <Loader2 className="w-4 h-4 animate-spin" />
                                              Génération...
                                            </>
                                          ) : existsInDb ? (
                                            <>
                                              <RefreshCw className="w-4 h-4" />
                                              Regénérer
                                            </>
                                          ) : (
                                            <>
                                              <Sparkles className="w-4 h-4" />
                                              Générer
                                            </>
                                          )}
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>

    {/* Éditeur de leçon (mode édition) */}
    {isEditorMode && currentLesson && (
      <LessonEditor
        lesson={currentLesson}
        onSave={handleUpdateLesson}
        onGenerateMore={handleAddMoreExercises}
        onClose={() => setIsEditorMode(false)}
        saving={updatingLesson}
        generating={addingExercises}
      />
    )}
    </>
  );
};

export default ProgramExplorer;
